// Castintech dashboard — Board view modülü.
// Aşama F (2026-05-25) — Board görünürlüğü (ilk sürüm, CHARTER v1.3 6 koltuk).
// Aşama G G2 (2026-05-29, BD-2026-05-28-002) — CHARTER v1.4 3-katmanlı Board drift fix.
//   Eski v1.3 yapısı (tek "aylık Cuma 18:00" board + "oy eşiği çoğunluk 3/5") YANLIŞ idi.
//   Doğru yapı: 3 katman (T1 Strategic quarterly / T2 Operating Council weekly Cuma 17:00 /
//   T3 Mini-Async 24-48h). castacc-gm artık VOTING (eski "0 oy gözlemci" düzeltildi);
//   efqm-gm (CQO) + Sapphire + Maestro advisor non-voting.
//
// İçerik:
//   - 3 katman kartı (T1/T2/T3): isim, sıklık, kompozisyon, quorum/eşik, kapsam
//   - T1 6 voting koltuk + advisor non-voting satırı
//   - Son 5 tutanak (wiki/decisions/board/{strategic,operating,async}/*-tutanak.md —
//     /castintech-api/wiki/files endpoint, alt-klasör başına ayrı çağrı)
//   - Sonraki Board countdown (T2 weekly Cuma 17:00 TRT — düzeltildi)
//   - Reserved Matters (T1-only) listesi
//
// API: GET /castintech-api/wiki/files?path=wiki/decisions/board/<katman>/&pattern=*-tutanak.md
// Bu endpoint Aşama F ile birlikte eklendi (notify/index.ts) — KORUNDU.
// CASA-2495 (2026-09-11) — fix: pattern *-minutes.md → *-tutanak.md (dosyalar Türkçe adlı,
// runCouncilTutanak `${date}-tutanak.md` üretiyor) + endpoint recursive olmadığından
// board/ yerine 3 alt-klasöre (strategic/operating/async) ayrı fetch + birleştir.

import { apiFetch } from '../api.js';
import { state } from '../state.js';
import { escHtml, escAttr, $ } from '../utils.js';
import { createSkeleton } from '../ui/state-display.js';

interface BoardMinuteFile {
  name: string;
  path: string;
  updated_at?: string;
  size?: number;
}

interface BoardFilesResponse {
  files: BoardMinuteFile[];
  path: string;
}

// Aşama G G2 (2026-05-29, BD-2026-05-28-002) — CHARTER v1.4 3-katmanlı Board drift fix.
// T1 Strategic Board — 6 voting koltuk. castacc-gm artık VOTING (pause olsa da Board oyu var,
// eski "0 oy gözlemci" YANLIŞ). efqm-gm/Sapphire/Maestro advisor non-voting (ayrı liste).
const T1_VOTING_SEATS = [
  { slug: 'yilmaz',      name: 'Baş Yılmaz', role: 'Chairman / CEO',       votes: 'Veto + tie-break', gem: '👤' },
  { slug: 'nexus',       name: 'Nexus',       role: 'Süper-GM · Castagent', votes: '1',                gem: '🔷' },
  { slug: 'sentinel',    name: 'Sentinel',    role: 'Castintech GM',        votes: '1',                gem: '🛡️' },
  { slug: 'castmart-gm', name: 'Castmart-GM', role: 'Castmart GM',          votes: '1',                gem: '🛒' },
  { slug: 'iq7s-gm',     name: 'IQ7s-GM',     role: 'IQ7s GM',              votes: '1',                gem: '🧠' },
  { slug: 'castacc-gm',  name: 'Castacc-GM',  role: 'Castacc GM (pause)',   votes: '1',                gem: '📊' },
];

// Advisor non-voting (T1 + T2) — CHARTER v1.4 § 2.1 (BD-002).
const ADVISORS_NON_VOTING = [
  { slug: 'efqm-gm',  name: 'efqm-gm', role: 'CQO · EFQM/kalite/memnuniyet', gem: '⭐' },
  { slug: 'sapphire', name: 'Sapphire', role: 'CFO · finans/fiyat/bütçe',     gem: '💠' },
  { slug: 'maestro',  name: 'Maestro',  role: 'CTOO · eko-sistem mimari',     gem: '🎼' },
];

// 3 katman özet — CHARTER v1.4 "3-Katmanlı Board Mimarisi" (§ 2.1 sonrası).
interface BoardLayer {
  tier: string;
  name: string;
  cadence: string;
  composition: string;
  quorum: string;
  scope: string;
  minutesDir: string;
}

const BOARD_LAYERS: BoardLayer[] = [
  {
    tier: 'T1',
    name: 'Strategic Board',
    cadence: 'Quarterly + ad-hoc reserved matters',
    composition: '6 voting koltuk (Yılmaz + Nexus + Sentinel + castmart-gm + iq7s-gm + castacc-gm) · efqm-gm/Sapphire/Maestro advisor non-voting',
    quorum: 'Yılmaz veto + tie-break · 4 GM eşit oy',
    scope: 'Yeni şirket, C-level atama, CHARTER §2/§5 patch, >%20 bütçe artışı, M&A, EFQM stratejik scoring, Recognition C2K',
    minutesDir: 'wiki/decisions/board/strategic/',
  },
  {
    tier: 'T2',
    name: 'Operating Council',
    cadence: 'Weekly · Cuma 17:00 TRT',
    composition: '13 voting üye: Nexus (chair) + 4 GM + 8 C-level (Atlas, Pearl, Prism, Vanta, Sapphire, Maestro, Onyx, efqm-gm)',
    quorum: 'Quorum 9/13',
    scope: 'Cross-GM KPI, escalation, mini-bütçe (<%20 realokasyon), müşteri P1, RADAR weekly review, operasyonel skill drift',
    minutesDir: 'wiki/decisions/board/operating/',
  },
  {
    tier: 'T3',
    name: 'Mini-Async Board',
    cadence: 'Trigger · 24-48h asenkron',
    composition: '2/4 GM + Yılmaz + ilgili domain C-level (örn. RLS için Prism)',
    quorum: '2/4 GM + Yılmaz onayı',
    scope: 'Tek-domain karar (RLS PR, SaaS pilot start, deploy unblock, castmart-lead C2 tarzı)',
    minutesDir: 'wiki/decisions/board/async/',
  },
];

// Reserved Matters (T1-only) — CHARTER v1.4. T2/T3 görüşemez.
const RESERVED_MATTERS = [
  'Yeni iş kolu / şirket açılışı veya kapanışı',
  'C-level atama (yeni GM, yeni Director seviye ajan)',
  'CHARTER § 2 / § 5 / § 7 patch (governance değişikliği)',
  'Bütçe artışı > %20 (yıllık konsolide MRR\'a göre)',
  'M&A (yatırım alma, ortaklık, satın alma)',
  'EFQM stratejik scoring (quarterly RADAR composite < 3.5/5 alarmı)',
  'Yeni Board üyesi kabulü (bağımsız üye, 7. koltuk önerisi)',
  'Reserved matter listesinin kendisi',
];

// Aşama G G2 (2026-05-29, BD-2026-05-28-002) — CHARTER v1.4 drift fix.
// Sonraki T2 Operating Council: weekly Cuma 17:00 TRT (eski "aylık son Cuma 18:00" YANLIŞ idi).
function nextOperatingCouncil(): Date {
  const now = new Date();
  // TRT = UTC+3; Cuma 17:00 TRT = 14:00 UTC.
  const trt = new Date(now.getTime() + 3 * 60 * 60 * 1000);
  // Bu haftanın (TRT) Cuma gününü bul (0=Sun, 5=Fri).
  const day = trt.getUTCDay();
  let addDays = (5 - day + 7) % 7;
  let d = new Date(Date.UTC(trt.getUTCFullYear(), trt.getUTCMonth(), trt.getUTCDate() + addDays, 14, 0, 0));
  // Bu haftanın Cuma'sı geçtiyse gelecek haftaya geç.
  if (d.getTime() <= now.getTime()) {
    d = new Date(d.getTime() + 7 * 86400000);
  }
  return d;
}

function formatCountdown(target: Date): string {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return 'Bugün 17:00 TRT';
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d} gün ${h} saat`;
  if (h > 0) return `${h} saat ${m} dk`;
  return `${m} dakika`;
}

// Dosya adından tarih çıkar (örn: 2026-05-25-asenkron-mini-board-onaylar.md)
function parseDateFromFilename(name: string): string {
  const m = name.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : '—';
}

// Dosya adından başlık üret
function titleFromFilename(name: string): string {
  return name
    .replace(/^\d{4}-\d{2}-\d{2}-/, '')
    .replace(/\.md$/, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

let _minuteFiles: BoardMinuteFile[] = [];
let _lastError: string | null = null;
let _loaded = false;

export async function loadBoard(): Promise<void> {
  // F4-A — İlk yüklemede skeleton (board sayfası açıksa).
  if (!_loaded && state.currentPage === 'board') {
    const root = $('board-content');
    if (root) {
      root.innerHTML = '';
      const sk = createSkeleton({ rows: 5, height: '80px', rowGap: '12px' });
      root.appendChild(sk);
    }
  }
  try {
    // CASA-2495 — endpoint recursive değil ve tutanaklar board/ altında 3 alt-klasörde
    // (strategic/operating/async) duruyor; her alt-klasöre ayrı istek atıp birleştiriyoruz.
    const dirs = BOARD_LAYERS.map(l => l.minutesDir);
    const responses = await Promise.all(dirs.map(dir =>
      apiFetch(`/castintech-api/wiki/files?path=${encodeURIComponent(dir)}&pattern=*-tutanak.md`)
    ));
    const allFiles: BoardMinuteFile[] = [];
    for (const r of responses) {
      if (!r.ok) {
        throw new Error('wiki/files HTTP ' + r.status);
      }
      const d = await r.json() as BoardFilesResponse;
      allFiles.push(...(d.files || []));
    }
    _minuteFiles = allFiles.sort((a, b) => b.name.localeCompare(a.name)).slice(0, 5);
    _lastError = null;
  } catch (e) {
    // F4-A — Önceki davranış: sessiz fail. Yeni: kullanıcıya görünür hata + retry.
    _lastError = (e as Error).message || 'Tutanak dosyaları yüklenirken sorun çıktı';
    _minuteFiles = [];
    console.warn('loadBoard failed:', _lastError);
  } finally {
    _loaded = true;
  }
  if (state.currentPage === 'board') renderBoard();
}

export function renderBoard(): void {
  const root = $('board-content');
  if (!root) return;

  // Aşama G G2 (2026-05-29, BD-2026-05-28-002) — alt başlık v1.4 3-katmanlı yapıyı yansıtır.
  const sub = $('board-sub');
  if (sub) sub.textContent = `CHARTER v1.4 · 3-katmanlı Board (T1/T2/T3) · T2 Operating Council weekly Cuma 17:00 TRT`;

  const nextCouncil = nextOperatingCouncil();
  const countdown = formatCountdown(nextCouncil);
  const nextDateStr = nextCouncil.toLocaleDateString('tr-TR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'Europe/Istanbul' });

  // T1 voting koltuk kartları.
  const seatsHtml = T1_VOTING_SEATS.map(s => `
    <div class="board-seat" title="${escAttr(s.role)}">
      <div class="board-seat-gem">${s.gem}</div>
      <div class="board-seat-name">${escHtml(s.name)}</div>
      <div class="board-seat-role">${escHtml(s.role)}</div>
      <div class="board-seat-votes">Oy: <strong>${escHtml(s.votes)}</strong></div>
    </div>`).join('');

  // Advisor non-voting satırı.
  const advisorsHtml = ADVISORS_NON_VOTING.map(a => `
    <span class="board-advisor-chip" title="${escAttr(a.role)}">${a.gem} ${escHtml(a.name)} <em>· non-voting</em></span>`).join('');

  // 3 katman kartları (T1/T2/T3).
  const layersHtml = BOARD_LAYERS.map(l => `
    <div class="board-layer-card" title="${escAttr(l.name)}">
      <div class="board-layer-head">
        <span class="board-layer-tier">${escHtml(l.tier)}</span>
        <span class="board-layer-name">${escHtml(l.name)}</span>
      </div>
      <div class="board-layer-row"><span class="board-layer-key">Sıklık</span> ${escHtml(l.cadence)}</div>
      <div class="board-layer-row"><span class="board-layer-key">Kompozisyon</span> ${escHtml(l.composition)}</div>
      <div class="board-layer-row"><span class="board-layer-key">Quorum/Eşik</span> ${escHtml(l.quorum)}</div>
      <div class="board-layer-row"><span class="board-layer-key">Kapsam</span> ${escHtml(l.scope)}</div>
      <div class="board-layer-minutes">📁 ${escHtml(l.minutesDir)}</div>
    </div>`).join('');

  // Reserved Matters listesi.
  const reservedHtml = RESERVED_MATTERS.map(r => `
    <div class="board-rule-row">• ${escHtml(r)}</div>`).join('');

  // Tutanak listesi — F4-A: error durumu ayrı, empty ayrı.
  let minutesHtml: string;
  if (_lastError) {
    minutesHtml = `<div class="ds-error" role="alert" style="margin:12px 0">
      <div class="ds-error-icon" aria-hidden="true">⚠️</div>
      <div class="ds-error-title">Tutanak listesi yüklenemedi</div>
      <div class="ds-error-desc">Bağlanırken sorun çıktı: ${escHtml(_lastError)}</div>
      <button class="ds-error-retry" type="button" id="board-retry-btn">Tekrar dene</button>
    </div>`;
  } else if (_minuteFiles.length) {
    minutesHtml = _minuteFiles.map(f => `
      <div class="board-minute-row">
        <span class="board-minute-date">${escHtml(parseDateFromFilename(f.name))}</span>
        <span class="board-minute-title">${escHtml(titleFromFilename(f.name))}</span>
      </div>`).join('');
  } else {
    minutesHtml = '<div class="empty-sub" style="padding:12px">Henüz tutanak yok — wiki/decisions/board/<katman>/*-tutanak.md formatında eklenince burada görünür.</div>';
  }

  root.innerHTML = `
    <div class="board-grid">

      <div class="board-section">
        <div class="board-section-title">📅 Sonraki T2 Operating Council</div>
        <div class="board-countdown-card">
          <div class="board-countdown-val">${escHtml(countdown)}</div>
          <div class="board-countdown-date">${escHtml(nextDateStr)} · Cuma 17:00 TRT</div>
          <div class="board-countdown-sub">CHARTER v1.4 — Operating Council weekly · T1 Strategic quarterly</div>
        </div>
      </div>

      <div class="board-section board-wide">
        <div class="board-section-title">🏛️ 3-Katmanlı Board (T1/T2/T3)</div>
        <div class="board-layers">${layersHtml}</div>
      </div>

      <div class="board-section board-seats-section board-wide">
        <div class="board-section-title">🪑 T1 Strategic Board — Voting Koltuklar (${T1_VOTING_SEATS.length})</div>
        <div class="board-seats">${seatsHtml}</div>
        <div class="board-advisors" style="margin-top:10px">
          <span style="font-size:11px;color:var(--muted)">Advisor (non-voting, T1+T2):</span>
          ${advisorsHtml}
        </div>
      </div>

      <div class="board-section board-wide">
        <div class="board-section-title">📋 Son Tutanaklar</div>
        <div class="board-minutes">${minutesHtml}</div>
        <div style="margin-top:8px;font-size:11px;color:var(--muted)">wiki/decisions/board/{strategic,operating,async}/ klasörlerindeki *-tutanak.md dosyaları · tarih DESC</div>
      </div>

      <div class="board-section board-wide">
        <div class="board-section-title">🔒 Reserved Matters (T1-only)</div>
        <div class="board-rule-list">${reservedHtml}</div>
        <div style="margin-top:6px;font-size:11px;color:var(--muted)">CHARTER v1.4 — T2/T3 bu konuları görüşemez · yanlış katman → Onyx audit [wrong-layer] issue</div>
      </div>

    </div>`;

  // F4-A — Tutanak listesi hatası varsa retry button bind.
  if (_lastError) {
    const btn = root.querySelector<HTMLButtonElement>('#board-retry-btn');
    if (btn) btn.addEventListener('click', () => { void loadBoard(); });
  }
}
